import * as THREE from 'three';
import type { FacilityRoom } from './FacilityDressing';

export interface BakeLight {
  position: THREE.Vector3;
  color: THREE.Color;
  intensity: number;
  range: number;
  roomId?: string;
  /** Fluorescents only — omitted from the fill-only (OFF) bake. */
  kind?: 'lamp' | 'fill';
}

const _world = new THREE.Vector3();
const _wnrm = new THREE.Vector3();
const _nmat = new THREE.Matrix3();
const _col = new THREE.Color();
const _matCache = new WeakMap<THREE.Material, THREE.Material>();

const SEG = 1.85;
const SEG_MAX = 8;

/** Corner-lit brushes like GE: extra verts on big faces so lamp pools show up. */
export function segmentedBox(sx: number, sy: number, sz: number): THREE.BoxGeometry {
  const wx = Math.min(SEG_MAX, Math.max(1, Math.round(sx / SEG)));
  const wy = Math.min(SEG_MAX, Math.max(1, Math.round(sy / SEG)));
  const wz = Math.min(SEG_MAX, Math.max(1, Math.round(sz / SEG)));
  return new THREE.BoxGeometry(sx, sy, sz, wx, wy, wz);
}

export function bakeFacilityLighting(
  root: THREE.Object3D,
  lights: BakeLight[],
  rooms: FacilityRoom[],
): void {
  for (const L of lights) {
    if (!L.roomId) L.roomId = roomAt(rooms, L.position)?.id;
  }
  root.updateMatrixWorld(true);
  root.traverse((o) => {
    if ((o as THREE.InstancedMesh).isInstancedMesh) {
      bakeInstanced(o as THREE.InstancedMesh, lights, rooms);
      return;
    }
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    if (mesh.name === 'bath_mirror' || mesh.userData.destructible === 'glass') return;
    if (mesh.userData.alarmLamp || mesh.userData.skipBake) return;
    const mat0 = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    if (mat0 instanceof THREE.MeshBasicMaterial && mat0.visible === false) return;
    ensureUniqueGeo(mesh);
    bakeMesh(mesh, lights, rooms);
    convertMaterial(mesh);
  });
  root.traverse((o) => {
    if (o.userData.destructible === 'lamp' && !o.userData.alarmBeacon) {
      o.userData.bakeRoomId = roomAt(rooms, o.position)?.id ?? '';
    }
  });
}

function ensureUniqueGeo(mesh: THREE.Mesh): void {
  const geo = mesh.geometry;
  if (!geo || geo.userData.bakeOwned) return;
  if (geo instanceof THREE.BoxGeometry) {
    const sx = Math.abs(mesh.scale.x) * (geo.parameters.width || 1);
    const sy = Math.abs(mesh.scale.y) * (geo.parameters.height || 1);
    const sz = Math.abs(mesh.scale.z) * (geo.parameters.depth || 1);
    if (sx > SEG || sy > SEG || sz > SEG) {
      const unit = Math.abs(mesh.scale.x) !== 1 || Math.abs(mesh.scale.y) !== 1 || Math.abs(mesh.scale.z) !== 1;
      mesh.geometry = unit
        ? new THREE.BoxGeometry(
            1,
            1,
            1,
            Math.min(SEG_MAX, Math.max(1, Math.round(Math.abs(mesh.scale.x) / SEG))),
            Math.min(SEG_MAX, Math.max(1, Math.round(Math.abs(mesh.scale.y) / SEG))),
            Math.min(SEG_MAX, Math.max(1, Math.round(Math.abs(mesh.scale.z) / SEG))),
          )
        : segmentedBox(sx, sy, sz);
      mesh.geometry.userData.bakeOwned = true;
      return;
    }
  }
  mesh.geometry = geo.clone();
  mesh.geometry.userData.bakeOwned = true;
}

function bakeMesh(mesh: THREE.Mesh, lights: BakeLight[], rooms: FacilityRoom[]): void {
  const geo = mesh.geometry;
  const pos = geo.attributes.position;
  const nrm = geo.attributes.normal;
  if (!pos) return;
  const mw = mesh.matrixWorld;
  _nmat.getNormalMatrix(mw);
  const colOn = new Float32Array(pos.count * 3);
  const colOff = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    _world.fromBufferAttribute(pos, i).applyMatrix4(mw);
    if (nrm) _wnrm.fromBufferAttribute(nrm, i).applyMatrix3(_nmat).normalize();
    else _wnrm.set(0, 1, 0);
    shade(_world, _wnrm, lights, rooms, _col, true);
    colOn[i * 3] = _col.r;
    colOn[i * 3 + 1] = _col.g;
    colOn[i * 3 + 2] = _col.b;
    shade(_world, _wnrm, lights, rooms, _col, false);
    colOff[i * 3] = _col.r;
    colOff[i * 3 + 1] = _col.g;
    colOff[i * 3 + 2] = _col.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colOn.slice(), 3));
  geo.userData.colorOn = colOn;
  geo.userData.colorOff = colOff;
  mesh.userData.bakeRoomId = roomAt(rooms, mesh.getWorldPosition(_world))?.id ?? '';
}

function bakeInstanced(mesh: THREE.InstancedMesh, lights: BakeLight[], rooms: FacilityRoom[]): void {
  const n = mesh.count;
  const colOn = new Float32Array(n * 3);
  const colOff = new Float32Array(n * 3);
  const roomIds: string[] = new Array(n);
  const dummy = new THREE.Object3D();
  const ceiling = mesh.name.startsWith('ceil_grid');
  _nmat.getNormalMatrix(mesh.matrixWorld);
  for (let i = 0; i < n; i++) {
    mesh.getMatrixAt(i, dummy.matrix);
    dummy.matrix.decompose(dummy.position, dummy.quaternion, dummy.scale);
    dummy.position.applyMatrix4(mesh.matrixWorld);
    if (ceiling) {
      _wnrm.set(0, -1, 0);
    } else {
      // Local +Z is the thin wall-facing axis after yaw; tip up so overhead lamps still register.
      _wnrm.set(0, 0.45, 0.89).applyQuaternion(dummy.quaternion);
    }
    _wnrm.applyMatrix3(_nmat).normalize();
    shade(dummy.position, _wnrm, lights, rooms, _col, true);
    colOn[i * 3] = _col.r;
    colOn[i * 3 + 1] = _col.g;
    colOn[i * 3 + 2] = _col.b;
    shade(dummy.position, _wnrm, lights, rooms, _col, false);
    colOff[i * 3] = _col.r;
    colOff[i * 3 + 1] = _col.g;
    colOff[i * 3 + 2] = _col.b;
    roomIds[i] = roomAt(rooms, dummy.position)?.id ?? '';
  }
  mesh.instanceColor = new THREE.InstancedBufferAttribute(colOn.slice(), 3);
  mesh.userData.colorOn = colOn;
  mesh.userData.colorOff = colOff;
  mesh.userData.bakeRoomIds = roomIds;
  convertMaterial(mesh);
}

function shade(
  p: THREE.Vector3,
  n: THREE.Vector3,
  lights: BakeLight[],
  rooms: FacilityRoom[],
  out: THREE.Color,
  includeLamps: boolean,
): void {
  const room = roomAt(rooms, p);
  const fy = room?.floorY ?? 0;
  const rid = room?.id;
  const amb = rid === 'stairdn' ? 0.4 : fy < -1 ? 0.28 : fy > 2 ? 0.3 : 0.26;
  out.setRGB(amb * 0.92, amb, amb * 1.06);
  for (const L of lights) {
    if (!includeLamps && L.kind === 'lamp') continue;
    if (L.roomId && rid && L.roomId !== rid) continue;
    const dx = L.position.x - p.x;
    const dy = L.position.y - p.y;
    const dz = L.position.z - p.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist > L.range || dist < 1e-4) continue;
    const att = (1 - dist / L.range) ** 2;
    const ndotl = (n.x * dx + n.y * dy + n.z * dz) / dist;
    const wrap = Math.max(0, ndotl) * 0.7 + 0.3;
    const k = L.intensity * att * wrap;
    out.r += L.color.r * k;
    out.g += L.color.g * k;
    out.b += L.color.b * k;
  }
  const h = p.y - fy;
  const up = Math.max(0, n.y);
  const floorProx = Math.max(0, 1 - h / 0.85);
  const ao = 1 - 0.26 * up * floorProx - 0.08 * floorProx * floorProx;
  out.r *= ao;
  out.g *= ao;
  out.b *= ao;
  out.r = Math.min(1.35, out.r);
  out.g = Math.min(1.35, out.g);
  out.b = Math.min(1.35, out.b);
}

function convertMaterial(mesh: THREE.Mesh): void {
  const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  const next = list.map((m) => cachedConvert(m));
  mesh.material = Array.isArray(mesh.material) ? next : next[0]!;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
}

function cachedConvert(mat: THREE.Material): THREE.Material {
  const hit = _matCache.get(mat);
  if (hit) return hit;
  if (mat instanceof THREE.MeshBasicMaterial) {
    mat.vertexColors = true;
    _matCache.set(mat, mat);
    return mat;
  }
  const std = mat as THREE.MeshStandardMaterial;
  if (std.emissiveIntensity > 0.18 && std.emissive && std.emissive.getHex() > 0) {
    std.vertexColors = true;
    _matCache.set(mat, std);
    return std;
  }
  const basic = new THREE.MeshBasicMaterial({
    color: std.color ? std.color.clone() : new THREE.Color(0xffffff),
    map: std.map ?? null,
    vertexColors: true,
    fog: true,
    transparent: std.transparent,
    opacity: std.opacity,
    side: std.side,
    alphaTest: std.alphaTest,
  });
  _matCache.set(mat, basic);
  return basic;
}

function roomAt(rooms: FacilityRoom[], p: THREE.Vector3): FacilityRoom | null {
  let best: FacilityRoom | null = null;
  let bestArea = Infinity;
  for (const r of rooms) {
    if (
      p.x < r.x0 - 0.2 ||
      p.x > r.x1 + 0.2 ||
      p.z < r.z0 - 0.2 ||
      p.z > r.z1 + 0.2 ||
      p.y < r.floorY - 0.45 ||
      p.y > r.floorY + r.height + 0.45
    ) {
      continue;
    }
    const area = Math.abs(r.x1 - r.x0) * Math.abs(r.z1 - r.z0);
    if (area < bestArea) {
      bestArea = area;
      best = r;
    }
  }
  return best;
}
